/**
 * Submit Claim Agent for Ameen Insurance Assistant
 * 
 * Handles the second half of the claim filing flow:
 * - Workshop Selection
 * - Claim Submission
 * - Completion
 * 
 * Activated AFTER Vehicle Inspection.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import {
    getContractedWorkshops,
    chooseContractedWorkshop,
    submitClaimSurvey
} from '../../services/claimService';
import { handleSearchFAQ } from './RouterAgent';
import { getCurrentLocation } from '../../lib/geocoding';

/**
 * Create Submit Claim Agent node
 */
export function createSubmitClaimAgent(voiceGender: VoiceGender): AgentNode {
    // Track state to prevent duplicate submissions
    let claimSubmitted = false;

    return {
        id: 'submit_claim_agent',
        name: 'Submit Claim Agent',
        description: 'Handles cleanup, workshop selection, and submission of claim',

        entryGuardrails: noEntryRequirements(),

        exitGuardrails: {
            transitions: new Map([
                ['go_to_policy', {
                    target: { agentId: 'policy_agent' },
                    guardrails: []
                }],
                ['go_to_router', {
                    target: { agentId: 'router_agent' },
                    guardrails: []
                }]
            ]),
            fallback: { agentId: 'router_agent' }
        },

        // Initial prompt handled by backend
        initialPrompt: 'متابعة بعد المعاينة.',

        /**
         * Called when this agent is activated
         */
        onActivate: async (context: AgentContext) => {
            logger.info('[SubmitClaimAgent] Activated');
            claimSubmitted = false;

            if (context.transitionData.damage_description) {
                logger.info('[SubmitClaimAgent] Received damage description:', context.transitionData.damage_description);
            } else {
                logger.warn('[SubmitClaimAgent] No damage description found in context!');
            }
        },

        /**
         * Handle tool calls
         */
        handleToolCall: async (
            toolName: string,
            args: Record<string, any>,
            context: AgentContext,
            uiState?: UIStateManager
        ): Promise<ToolCallResult> => {
            logger.tool('[SubmitClaimAgent] Tool called:', toolName, args);

            switch (toolName) {
                // ═══ ROUTING TOOLS ═══
                case 'go_to_router_agent':
                    return {
                        toolName,
                        args,
                        result: { success: true, message: 'Returning to menu' },
                        triggersTransition: true,
                        transitionTo: 'router_agent',
                        transitionDelayMs: -1,
                        transitionData: { return_to_menu: true },
                    };

                case 'go_to_policy_agent':
                    return {
                        toolName,
                        args,
                        result: { success: true, message: 'Switching to policy flow' },
                        triggersTransition: true,
                        transitionTo: 'policy_agent',
                        transitionDelayMs: -1
                    };

                // ═══ WORKSHOP TOOLS ═══
                case 'show_workshop_options':
                    if (uiState) {
                        uiState.setFlowStep('claim_workshop_options');
                    }
                    return {
                        toolName,
                        args,
                        result: {
                            success: true,
                            message: "Workshop options displayed. Waiting for user selection."
                        }
                    };

                case 'show_contracted_workshops_list':
                    return await handleShowContractedWorkshops(args, toolName, context, uiState);

                case 'show_non_contracted_workshop_form':
                    if (uiState) {
                        uiState.setFlowStep('claim_non_contracted_form');
                    }
                    return {
                        toolName,
                        args,
                        result: {
                            success: true,
                            message: "Form displayed. Waiting for submission."
                        }
                    };

                // ═══ SUBMISSION & COMPLETION ═══
                case 'submit_claim_survey_request':
                    return await handleSubmitClaimSurvey(args, toolName, context, uiState, claimSubmitted);

                case 'show_claim_number':
                    if (uiState) {
                        // Retrieve claim request number from context or session
                        const claimRequestNum = context.transitionData.claim_request_num ||
                            uiState.getFromSessionStorage('claim_request_num');

                        if (claimRequestNum) {
                            uiState.showPolicyNumber(
                                claimRequestNum,
                                'رقم المطالبة',
                                'تم تسجيل المطالبة بنجاح. يرجى حفظ رقم المطالبة:',
                                () => {
                                    logger.info('[SubmitClaimAgent] User confirmed claim number');
                                }
                            );
                        }
                    }
                    return {
                        toolName,
                        args,
                        result: {
                            success: true,
                            message: "Claim number displayed. Tell user to save this number."
                        }
                    };

                // ═══ DATA TOOLS ═══
                case 'search_faq':
                    return await handleSearchFAQ(args, toolName);

                default:
                    logger.warn('[SubmitClaimAgent] Unknown tool:', toolName);
                    return {
                        toolName,
                        args,
                        result: { success: false, error: `Unknown tool: ${toolName}` }
                    };
            }
        },

        onDeactivate: async (context: AgentContext) => {
            logger.info('[SubmitClaimAgent] Deactivated');
        }
    };
}

/**
 * Handle show_contracted_workshops_list tool
 */
async function handleShowContractedWorkshops(
    args: Record<string, any>,
    toolName: string,
    context: AgentContext,
    uiState?: UIStateManager
): Promise<ToolCallResult> {
    try {
        logger.info('[SubmitClaimAgent] Fetching contracted workshops');
        uiState?.showLoading?.('جاري جلب الورش المتعاقد معها...', 'api');

        // Try to get actual user location via geolocation
        let lat = 30.0444; // Default to Cairo
        let long = 31.2357;
        
        try {
            const loc = await getCurrentLocation();
            lat = loc.lat;
            long = loc.long;
            logger.info('[SubmitClaimAgent] Successfully retrieved real user location:', { lat, long });
        } catch (locError: any) {
            logger.warn('[SubmitClaimAgent] Geolocation failed, falling back to context or default:', locError.message);
            // Get user location from context as a secondary fallback
            const location = context.transitionData.userLocation;
            if (location) {
                lat = location.lat || lat;
                long = location.lng || long;
            }
        }

        const workshops = await getContractedWorkshops(lat, long);
        uiState?.hideLoading?.();
        logger.info('[SubmitClaimAgent] Found', workshops.length, 'contracted workshops');

        // Update UI state
        if (uiState) {
            uiState.setContractedWorkshops(workshops);
            uiState.setFlowStep('claim_contracted_workshops');
        }

        // Find nearest workshop
        const nearestWorkshop = workshops.find((w: any) => w.isNearest);

        let resultMessage = `Found ${workshops.length} contracted workshops.`;
        if (nearestWorkshop) {
            const distanceKm = nearestWorkshop.distance
                ? (nearestWorkshop.distance / 1000).toFixed(1)
                : 'unknown';
            resultMessage += ` Nearest workshop: ${nearestWorkshop.workshopName} (${distanceKm} km away).`;
        }

        return {
            toolName,
            args,
            result: {
                success: true,
                workshops,
                count: workshops.length,
                nearestWorkshop,
                message: resultMessage
            }
        };
    } catch (error: any) {
        uiState?.hideLoading?.();
        logger.error('[SubmitClaimAgent] Error fetching workshops:', error);
        return {
            toolName,
            args,
            result: {
                success: false,
                error: error.message || 'Failed to fetch contracted workshops'
            }
        };
    }
}

/**
 * Handle submit_claim_survey_request tool
 */
async function handleSubmitClaimSurvey(
    args: Record<string, any>,
    toolName: string,
    context: AgentContext,
    uiState?: UIStateManager,
    alreadySubmitted: boolean = false
): Promise<ToolCallResult> {
    // Prevent duplicate submissions
    if (alreadySubmitted) {
        logger.warn('[SubmitClaimAgent] Claim survey already submitted - ignoring duplicate call');
        return {
            toolName,
            args,
            result: {
                success: true,
                message: 'Claim survey was already submitted. Please continue with the next step.'
            }
        };
    }

    try {
        // Get damage description from args or context
        const damageDescription = args.damage_description ||
            context.transitionData.damage_description ||
            'No damage description provided';

        // Get policy number from context, UI state, or session storage (fallback)
        let policyNumber = context.transitionData.policy_number;

        if (!policyNumber && uiState) {
            // Try getting from UI state helper (primary source)
            const claimPolicyData = uiState.getClaimPolicyData();
            if (claimPolicyData?.policyNumber) {
                policyNumber = claimPolicyData.policyNumber;
                logger.info('[SubmitClaimAgent] Retrieved policy number from UI state:', policyNumber);
            } else {
                // Fallback: Try getting from session storage directly using correct key
                const sessionData = uiState.getFromSessionStorage('saaed_claim_data');
                if (sessionData?.policyNumber) {
                    policyNumber = sessionData.policyNumber;
                    logger.info('[SubmitClaimAgent] Retrieved policy number from session storage (saaed_claim_data):', policyNumber);
                }
            }
        }

        if (!policyNumber) {
            logger.error('[SubmitClaimAgent] No policy number available for claim submission. Context:', context.transitionData);
            return {
                toolName,
                args,
                result: {
                    success: false,
                    error: 'No policy number available. Please verify policy first.'
                }
            };
        }

        // Get survey images from UI state
        const images = uiState?.getClaimSurveyImages() || [];

        logger.info('[SubmitClaimAgent] Submitting claim survey:', {
            policyNumber,
            imageCount: images.length,
            damageDescription: damageDescription.substring(0, 50) + '...'
        });

        uiState?.showLoading?.('جاري تسجيل بلاغ الحادث...', 'api');
        const result = await submitClaimSurvey(policyNumber, images, damageDescription);
        logger.info('[SubmitClaimAgent] Claim survey submitted:', result);
        uiState?.hideLoading?.();

        // Save claim ID for later reference
        if (uiState && result.claimSurveyId) {
            uiState.saveToSessionStorage('claim_request_num', result.claimSurveyId);
        }

        // Store claim ID in transition data for access across agents
        if (result.claimSurveyId) {
            context.transitionData.claim_request_num = result.claimSurveyId;
        }

        return {
            toolName,
            args,
            result: {
                success: true,
                claimSurveyId: result.claimSurveyId,
                message: result.message
            }
        };
    } catch (error: any) {
        uiState?.hideLoading?.();
        logger.error('[SubmitClaimAgent] Error submitting claim survey:', error);
        return {
            toolName,
            args,
            result: {
                success: false,
                error: error.message || 'Failed to submit claim survey'
            }
        };
    }
}

